// How an email did: progress while sending, opens and clicks, who did what. Pause, resume or stop.

import { useEffect, useRef, useState } from 'preact/hooks';
import { api } from '../lib/api';
import { linkClick, navigate, useLocation } from '../lib/router';
import { Button, LoadError, Notice, Progress, Spinner, StatusChip, confirmBox, toast, toastError, useLoad } from '../lib/ui';
import { Icon } from '../lib/icons';
import { eta, fullName, initials, num, pct, plural, when } from '../lib/format';
import type { EmailStats, EmailStatus } from '../../shared/types';
import { LiveTaps, ReadyToBook, useCountUp, type Lead } from './ReportLive';
import { Butterfly } from '../parts/Butterfly';
import { useSettings } from '../lib/store';
import { PersonDrawer } from './Contacts';

interface ReportData {
  stats: EmailStats;
  email: { id: number; name: string; subject: string; status: EmailStatus; pause_reason: string | null; started_at: string | null; finished_at: string | null; scheduled_at: string | null };
}

interface Person {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  status: string;
  sent_at: string | null;
  opens: number;
  clicks: number;
  error: string | null;
}

type Filter = 'opened' | 'clicked' | 'unsubscribed' | 'bounced' | 'failed' | 'waiting';

export function Report({ id }: { id: number }) {
  const { query } = useLocation();
  const r = useLoad(() => api<ReportData>(`/api/emails/${id}/stats`), [id]);
  const [busy, setBusy] = useState<string | null>(null);
  const justSent = query.get('sent') === '1';
  const status = r.data?.email.status;
  const live = status === 'sending' || status === 'scheduled';
  const leads = useLoad(() => api<{ leads: Lead[] }>(`/api/emails/${id}/leads`).then((x) => x.leads), [id, r.data?.stats.clicked]);
  const sentShown = useCountUp(r.data?.stats.sent ?? 0);
  // Celebrate when the last email goes out while Jen is watching.
  const wasSending = useRef(false);
  const [finished, setFinished] = useState(false);
  useEffect(() => {
    if (status === 'sending') wasSending.current = true;
    else if (status === 'sent' && wasSending.current) setFinished(true);
  }, [status]);

  useEffect(() => {
    if (!live) return;
    const t = setInterval(() => r.reload(), 5000);
    return () => clearInterval(t);
  }, [live]);

  if (r.error) return <div class="wrap"><LoadError error={r.error} retry={r.reload} /></div>;
  if (!r.data) return <Spinner />;
  const { stats: s, email: e } = r.data;
  const done = s.sent + s.failed + s.skipped + s.unknown;

  const act = async (what: 'pause' | 'resume' | 'cancel') => {
    if (what === 'cancel') {
      const ok = await confirmBox({
        title: 'Stop sending?',
        body: `${plural(s.queued, 'person', 'people')} who haven't gotten it yet won't get it. This can't be undone. To stop for now, use Pause instead.`,
        ok: 'Stop for good',
        danger: true,
      });
      if (!ok) return;
    }
    setBusy(what);
    try {
      await api(`/api/emails/${id}/${what}`, { method: 'POST' });
      toast(what === 'pause' ? 'Paused. Nobody else gets it until you resume.' : what === 'resume' ? 'Sending again.' : 'Stopped.');
      await r.reload();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(null);
    }
  };

  const known = useKnownLinks();
  const linkName = (u: string) => known[prettyUrl(u)] || prettyUrl(u);

  const copy = async () => {
    try {
      const { id: newId } = await api<{ id: number }>(`/api/emails/${id}/duplicate`, { method: 'POST' });
      navigate(`/emails/${newId}/edit`);
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div class="wrap">
      <a href="/" class="back-link" onClick={(ev) => linkClick(ev, '/')}>
        <Icon name="back" size={16} /> Emails
      </a>
      <div class="page-head">
        <div>
          <h1 class="display">{e.name}</h1>
          <p class="lead">
            {e.subject} · <StatusChip status={e.status} />
          </p>
        </div>
        <div class="head-actions">
          {e.status === 'sending' && (
            <Button icon="pause" busy={busy === 'pause'} onClick={() => act('pause')}>
              Pause
            </Button>
          )}
          {e.status === 'paused' && (
            <Button kind="primary" icon="play" busy={busy === 'resume'} onClick={() => act('resume')}>
              Resume
            </Button>
          )}
          {(e.status === 'sending' || e.status === 'paused') && (
            <Button kind="quiet" icon="stop" class="danger-text" busy={busy === 'cancel'} onClick={() => act('cancel')}>
              Stop
            </Button>
          )}
          <Button icon="copy" onClick={copy}>
            Make a copy
          </Button>
        </div>
      </div>

      {/* "All sent" only when nothing is left to go out. Otherwise the card says what is still waiting. */}
      {finished && !s.queued ? <AllSent n={s.sent} /> : e.status === 'sent' && e.finished_at && s.sent > 0 && <AllSent n={s.sent} left={s.queued} book={leads.data?.length ?? 0} quiet />}

      {justSent && e.status === 'sending' && (
        <div class="celebrate">
          <div class="celebrate-ic">
            <Icon name="send" size={22} />
          </div>
          <div>
            <b>It's on its way.</b>
            <span>You can close this page. Sending keeps going on its own, and it pauses itself if anything looks wrong.</span>
          </div>
        </div>
      )}

      {e.status === 'paused' && e.pause_reason && <Notice kind="warn">{e.pause_reason}</Notice>}

      {(e.status === 'sending' || e.status === 'paused') && (
        <div class="progress-card">
          <div class="progress-top">
            <span>
              <b>{num(sentShown)}</b> of {num(s.total)} sent
              {e.status === 'sending' && s.etaMinutes != null && <span class="muted"> · {eta(s.etaMinutes)}</span>}
            </span>
            <span class="big-pct">{pct(done, s.total)}</span>
          </div>
          <Progress value={done} max={s.total} label="Progress" />
        </div>
      )}

      <div class="kpis">
        <Kpi label="Sent" value={num(sentShown)} sub={e.finished_at ? cap(when(e.finished_at)) : e.started_at ? `Started ${when(e.started_at)}` : ''} />
        <Kpi label="Opened" value={pct(s.opened, s.sent)} sub={`${num(s.opened)} people`} title="Some email apps open every email automatically, so this number runs high." />
        <Kpi label="Clicked" value={pct(s.clicked, s.sent)} sub={`${num(s.clicked)} people`} />
        <Kpi label="Unsubscribed" value={num(s.unsubscribed)} sub={s.sent && s.unsubscribed ? pct(s.unsubscribed, s.sent) : ''} />
      </div>

      {(s.bounced > 0 || s.failed + s.unknown > 0 || s.complained > 0) && (
        <p class="muted problems">
          <Icon name="alert" size={15} /> {[s.bounced && `${plural(s.bounced, 'bad address', 'bad addresses')} (they won't get future emails)`, s.failed + s.unknown && `${num(s.failed + s.unknown)} couldn't be sent`, s.complained && `${num(s.complained)} marked it as spam`].filter(Boolean).join(' · ')}
        </p>
      )}

      {leads.data && <ReadyToBook leads={leads.data} note="They tapped Book but have not booked yet. Call or text them first." />}

      {/* Only while it goes out. Once it is sent, Ready to book covers the same people. */}
      {(e.status === 'sending' || e.status === 'paused') && <LiveTaps id={id} live />}

      {s.links.length > 0 && (
        <section class="card">
          <h2 class="card-title">Links people tapped</h2>
          <div class="link-list">
            {s.links
              .slice()
              .sort((a, b) => b.clicks - a.clicks)
              .map((l) => {
                const max = Math.max(1, ...s.links.map((x) => x.clicks));
                return (
                  <div class="link-row" key={l.n}>
                    <span class="link-url" title={l.url}>
                      {linkName(l.url)}
                    </span>
                    <span class="link-bar">
                      <i style={{ width: `${(l.clicks / max) * 100}%` }} />
                    </span>
                    <b>{num(l.clicks)}</b>
                  </div>
                );
              })}
          </div>
        </section>
      )}

      <People id={id} stats={s} />
    </div>
  );
}

/** The moment it finishes: a burst of color and the final count. Motion stops for people who turn it off. */
function AllSent({ n, left = 0, book = 0, quiet = false }: { n: number; left?: number; book?: number; quiet?: boolean }) {
  // Quiet: the same card when she comes back later, without the burst or the wings opening.
  return (
    <div class={`all-sent${quiet ? ' all-sent-quiet' : ''}`} role="status">
      {!quiet && !left && (
        <div class="burst" aria-hidden="true">
          {Array.from({ length: 14 }, (_, i) => (
            <i key={i} style={{ '--a': `${(i * 360) / 14}deg`, '--d': `${70 + (i % 3) * 22}px` }} />
          ))}
        </div>
      )}
      <div class="all-sent-ic">
        <Butterfly size={30} dark open={!quiet && !left} />
      </div>
      <div>
        <b>{left ? `${num(n)} sent. ${num(left)} not sent yet.` : `All ${num(n)} sent.`}</b>
        <span>{quiet ? (book ? `${plural(book, 'client wants', 'clients want')} to book.` : 'Nice work.') : 'Nice work. Taps show up below as they come in.'}</span>
      </div>
    </div>
  );
}

function cap(t: string): string {
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** Plain names for the spa's own links, so "Booking page" shows instead of a long web address. */
function useKnownLinks(): Record<string, string> {
  const s = useSettings()?.settings;
  const out: Record<string, string> = {};
  if (!s) return out;
  const add = (u: string | undefined, name: string) => {
    if (u && u.trim()) out[prettyUrl(u.trim())] = name;
  };
  add(s.website, 'Website');
  add(s.instagram, 'Instagram');
  add(s.booking_url, 'Booking page');
  return out;
}

function Kpi({ label, value, sub, title }: { label: string; value: string; sub?: string; title?: string }) {
  return (
    <div class="kpi" title={title}>
      <small>{label}</small>
      <b>{value}</b>
      {/* An empty line keeps all four numbers on one baseline. */}
      <em>{sub || ' '}</em>
    </div>
  );
}

function prettyUrl(u: string): string {
  try {
    const x = new URL(u);
    return (x.hostname.replace(/^www\./, '') + x.pathname).replace(/\/$/, '');
  } catch {
    return u;
  }
}

function People({ id, stats }: { id: number; stats: EmailStats }) {
  const tabs: { k: Filter; label: string; n: number }[] = (
    [
      { k: 'opened', label: 'Opened', n: stats.opened },
      { k: 'clicked', label: 'Clicked', n: stats.clicked },
      { k: 'unsubscribed', label: 'Unsubscribed', n: stats.unsubscribed },
      { k: 'bounced', label: 'Bad address', n: stats.bounced },
      { k: 'failed', label: "Couldn't send", n: stats.failed + stats.unknown },
      { k: 'waiting', label: 'Not sent yet', n: stats.queued },
    ] as { k: Filter; label: string; n: number }[]
  ).filter((t) => t.n > 0 || t.k === 'opened');
  const [f, setF] = useState<Filter>(stats.clicked ? 'clicked' : 'opened');
  const list = useLoad(() => api<{ people: Person[] }>(`/api/emails/${id}/people?filter=${f}`).then((r) => r.people), [id, f, stats.opened, stats.clicked]);
  const [all, setAll] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  useEffect(() => setAll(false), [f]);
  useEffect(() => {
    if (openId != null && !tags.length) api<{ tags: { tag: string }[] }>('/api/tags').then((r) => setTags(r.tags.map((t) => t.tag)), () => {});
  }, [openId]);
  const LIMIT = 10;
  const shown = list.data ? (all ? list.data : list.data.slice(0, LIMIT)) : [];
  // A count only says something when it is more than one.
  const meta = (p: Person) => (p.clicks > 1 ? plural(p.clicks, 'click') : p.clicks ? '' : p.opens > 1 ? plural(p.opens, 'open') : '');

  return (
    <section class="card people-card">
      <h2 class="card-title">People</h2>
      <div class="tabs small">
        {tabs.map((t) => (
          <button key={t.k} class={f === t.k ? 'on' : ''} onClick={() => setF(t.k)}>
            {t.label} <i>{num(t.n)}</i>
          </button>
        ))}
      </div>
      {!list.data ? (
        <Spinner />
      ) : list.data.length === 0 ? (
        <p class="muted pad">Nobody yet. Opens usually come in over the first day.</p>
      ) : (
        <div class="people">
          {shown.map((p) => (
            <button class="person person-open" key={p.id} onClick={() => setOpenId(p.id)}>
              <span class="avatar">{initials(p)}</span>
              <span class="person-text">
                <b>{fullName(p)}</b>
                <small>{p.first_name || p.last_name ? p.email : p.error || ''}</small>
              </span>
              <span class="person-meta">{p.error && !p.clicks && !p.opens ? <span class="bad-text">{p.error.slice(0, 60)}</span> : meta(p)}</span>
              <Icon name="next" size={16} />
            </button>
          ))}
          {list.data.length > LIMIT && (
            <button class="link-btn" onClick={() => setAll(!all)}>
              {all ? 'Show fewer' : `Show all ${num(list.data.length)}`}
            </button>
          )}
          {all && list.data.length >= 500 && <p class="muted pad">Showing the first 500.</p>}
        </div>
      )}
      {openId != null && <PersonDrawer id={openId} allTags={tags} onClose={() => setOpenId(null)} onChanged={list.reload} />}
    </section>
  );
}
